import type { SearchHistoryItem } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { formatDistance } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { HistoryIcon, Loader2Icon, XIcon } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import useSWRImmutable from "swr/immutable";

import { useGlobalStore, useSearch } from "~/hooks";
import useKeyboardShortcut from "~/hooks/useKeyboardShortcut";
import fetcher from "~/utils/fetcher.client";
import { getPlatformControlKey, getRoutePrefix } from "~/utils/helpers";

const SearchModal = () => {
	const { isSearchModalOpen, toggleSearchModal } = useGlobalStore();
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");

	useDebounce(
		() => {
			setDebouncedQuery(query);
		},
		500,
		[query],
	);

	const { userId } = useAuth();

	const { data: searchHistory, mutate } = useSWRImmutable<SearchHistoryItem[]>(
		userId && `/search/history`,
	);

	const addSearchHistory = async (query: string) => {
		if (!userId || !query) return;

		await mutate(
			(await fetcher(`/search/history`, {
				method: "POST",
				data: {
					query,
				},
			})) as SearchHistoryItem[],
			{
				revalidate: true,
				populateCache: false,
			},
		);
	};

	const { data, isLoading, isValidating } = useSearch({
		query: debouncedQuery,
	});

	useKeyboardShortcut("k", toggleSearchModal);

	useEffect(() => {
		isSearchModalOpen && document.getElementById("search-input")?.focus();
	}, [isSearchModalOpen]);

	return (
		<AnimatePresence>
			{isSearchModalOpen && (
				<Dialog
					key="search-modal"
					static
					open={isSearchModalOpen}
					onClose={toggleSearchModal}
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/50 dark:bg-black/40"
					/>

					<motion.div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto phablet:pt-48">
						<DialogPanel
							as={motion.div}
							layout="position"
							initial={{ opacity: 0, y: 20 }}
							animate={{
								opacity: 1,
								y: 0,
								transition: { duration: 0.2 },
							}}
							exit={{ opacity: 0, y: 20, transition: { duration: 0.15 } }}
							className="no-scrollbar relative phablet:mx-auto flex h-full phablet:h-auto phablet:max-h-[60vh] w-full phablet:max-w-2xl flex-col overflow-hidden overflow-y-auto phablet:rounded-xl border bg-white shadow-black/10 shadow-xl dark:bg-slate-800 dark:shadow-black/10"
						>
							<div className="flex phablet:hidden items-center justify-between px-4 py-3">
								<DialogTitle className="font-semibold text-2xl">
									Search
								</DialogTitle>
								<div
									className="relative cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:brightness-95 sm:-right-1 dark:bg-slate-700"
									onClick={toggleSearchModal}
								>
									<XIcon size={22} />
								</div>
							</div>

							<div className="sticky top-0 flex items-center gap-3 phablet:gap-4 bg-white phablet:px-6 px-4 phablet:py-4 py-1 dark:bg-slate-800">
								<input
									autoFocus
									type="text"
									value={query}
									autoComplete="off"
									id="search-input"
									onChange={({ target: { value } }) => setQuery(value)}
									className="!appearance-none !p-0 flex-1 rounded-none border-none text-lg shadow-none focus:ring-0 dark:bg-transparent"
									placeholder="Start typing to search"
								/>

								{isLoading || isValidating ? (
									<div className="rounded-full font-medium hover:underline">
										<Loader2Icon
											size={20}
											className="animate-spin text-slate-500"
										/>
									</div>
								) : query ? (
									<button
										onClick={() => setQuery("")}
										className="rounded-full font-medium hover:underline"
									>
										Clear
									</button>
								) : (
									<div className="-mx-1.5 -my-0.5 rounded-lg border bg-slate-50 px-2 py-0.5 dark:bg-slate-800">
										{getPlatformControlKey()} + K
									</div>
								)}
							</div>

							{data && query ? (
								<div
									className="phablet:-mt-3 phablet:mb-0 flex flex-col gap-2 phablet:gap-0 p-2 phablet:p-3"
									key={"search-results"}
								>
									{data?.hits?.length ? (
										data.hits?.map((item) => (
											<Link
												href={`/${getRoutePrefix(
													(item.document as any)._type.toUpperCase(),
												)}/${(item.document as any).slug}`}
												onClick={() => {
													addSearchHistory(query);
													setQuery("");
													toggleSearchModal();
												}}
												key={(item.document as any).slug}
												className="rounded-lg phablet:px-4 px-2.5 phablet:py-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
											>
												<div className="flex items-center gap-2">
													<h3 className="phablet:text-lg">
														<span className="shrink-0 font-medium capitalize">
															{(item.document as any)._type.toLowerCase()}:
														</span>{" "}
														{(item.document as any).title}
													</h3>
												</div>

												{item.highlights?.[0].snippet && (
													<p
														dangerouslySetInnerHTML={{
															__html: item.highlights?.[0].snippet,
														}}
													/>
												)}
											</Link>
										))
									) : (
										<div className="flex h-28 items-center justify-center">
											<p className="font-medium text-lg">No results found</p>
										</div>
									)}
								</div>
							) : (
								searchHistory &&
								searchHistory.length > 0 && (
									<div className="phablet:-mt-3 phablet:mb-0 flex flex-col gap-2 phablet:gap-1 p-2 phablet:p-3">
										{searchHistory.map((item, index) => (
											<button
												key={index}
												onClick={() => {
													setQuery(item.query);
													setDebouncedQuery(item.query);
												}}
												className="rounded-lg phablet:px-4 px-2.5 phablet:py-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
											>
												<div className="flex items-center gap-2.5">
													<HistoryIcon className="size-5 shrink-0" />

													<p className="line-clamp-1 text-left font-medium">
														{item.query}
													</p>

													<span className="ml-auto shrink-0 text-sm">
														{formatDistance(
															new Date(item.timestamp),
															new Date(),
															{ addSuffix: true },
														)}
													</span>
												</div>
											</button>
										))}
									</div>
								)
							)}
						</DialogPanel>
					</motion.div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default SearchModal;
